import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import { useDispatch, useSelector } from 'react-redux';
import RecipeBoardActionsPopupMenu from './recipe_board_actions_popup_menu';
import RecipesBoardEditModal from './recipes_board_edit_modal';
import { ActivityObjectType, ContainerType } from '../../API';
import {
  useCreateSharedBoardMutation,
  useCreateSharedMealMutation,
  useDeleteMealMutation,
  useDeleteSharedMealMutation,
  useDeleteSmorgBoardMutation,
  useGetSharedProgrammesQuery,
  useGetSpaceQuery,
  useLazyGetDistilledSharedMealsByParentQuery,
  useLazyGetFullMealsByParentQuery,
  useUpdateSharedBoardMutation,
  useUpdateSmorgBoardMutation,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { otherUserActiveOnResourceSelector } from '../../features/server_status/server_status_slice';
import {
  useGetEndUserSpaceQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  publishRecipesBoardToSpace,
  removeRecipesBoard,
  unpublishRecipesBoardFromSpace,
} from '../../operations/recipes_operations';
import { userIsCreator } from '../../services/users';
import {
  currentUserSelector,
  userFriendlyNameForActivitySignalSelector,
} from '../../features/user_id/user_id_slice';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { sharedProgrammesWhichReferenceMeals } from '../../services/programmes';
import { isBoardPublishedToSpace } from '../../services/smorg_board';

const RecipesNavigationBar = ({ selectedRecipesBoardId, openInRenameMode }) => {
  const [recipesBoardEditMode, setRecipesBoardEditMode] =
    useState(openInRenameMode);

  useEffect(() => {
    setRecipesBoardEditMode(openInRenameMode);
  }, [openInRenameMode]);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const selectedRecipesBoard = (ownedRecipesBoards || []).find(
    (b) => b.id === selectedRecipesBoardId,
  );

  const selectedRecipesBoardTitle = selectedRecipesBoard?.title;
  const isRecipesBoardEmbeddedInProgramme =
    selectedRecipesBoard?.embeddedInContainerType === ContainerType.PROGRAMME &&
    !!selectedRecipesBoard?.embeddedInContainerID;

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: healthProSpace } = useGetSpaceQuery();

  const { data: currentSpace } = useGetEndUserSpaceQuery(undefined, {
    skip: !userProfile || isSmorgStudio,
  });

  const mealIDs = (selectedRecipesBoard?.menus || []).flatMap(
    (menu) => menu.mealIDs,
  );

  const { data: sharedProgrammes } = useGetSharedProgrammesQuery(
    healthProSpace?.id,
    {
      skip: !healthProSpace,
    },
  );

  const sharedProgrammesLinked = sharedProgrammesWhichReferenceMeals(
    mealIDs,
    sharedProgrammes || [],
  );

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [deleteMeal] = useDeleteMealMutation();

  const [deleteSmorgBoard] = useDeleteSmorgBoardMutation();

  const [lazyGetFullMealsByParent] = useLazyGetFullMealsByParentQuery();

  const [lazyGetDistilledSharedMealsByParent] =
    useLazyGetDistilledSharedMealsByParentQuery();

  const [createSharedBoard] = useCreateSharedBoardMutation();

  const [updateSharedBoard] = useUpdateSharedBoardMutation();

  const [createSharedMeal] = useCreateSharedMealMutation();

  const [deleteSharedMeal] = useDeleteSharedMealMutation();

  const [updateSpace] = useUpdateSpaceMutation();

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const spaceAssignedSharedBoardIDs = currentSpace?.sharedRecipesBoardIDs || [];

  const isProductizedSharedBoard = spaceAssignedSharedBoardIDs.includes(
    selectedRecipesBoardId,
  );

  const onEditRecipesBoard = () => {
    setRecipesBoardEditMode(true);
  };

  /** TODO duplicate code with TopNavBarMobileRecipes */
  const updateRecipesBoard = async (updatedRecipesBoard) => {
    setRecipesBoardEditMode(false);
    await updateSmorgBoard(updatedRecipesBoard);
    if (isSmorgStudio) {
      reportAccountActivitySignalOperation({
        spaceID: healthProSpace.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.RECIPES,
        lastActivityObjectID: updatedRecipesBoard.id,
      });
    }
    // Remove the ?rename from URL
    navigate(`/recipes/${selectedRecipesBoardId}`);
  };

  /** TODO duplicate code with TopNavBarMobileRecipes */
  const onDuplicateRecipesBoard = () => {
    console.log('onDuplicateRecipesBoard');
    // TODO implement
  };

  /** TODO duplicate code with TopNavBarMobileRecipes */
  const onShareRecipesBoard = () => {
    console.log('onShareRecipesBoard');
    navigate(`/recipes/${selectedRecipesBoardId}/share`);
  };

  /** TODO duplicate code with TopNavBarMobileRecipes */
  const onDeleteRecipesBoard = () => {
    const recipesBoard = ownedRecipesBoards.find(
      (b) => b.id === selectedRecipesBoardId,
    );
    const firstRemainingRecipesBoard = ownedRecipesBoards.find(
      (b) => b.id !== selectedRecipesBoardId,
    );
    removeRecipesBoard(
      recipesBoard,
      healthProSpace,
      deleteMeal,
      deleteSmorgBoard,
      updateSpace,
      dispatch,
    );
    if (isSmorgStudio) {
      reportAccountActivitySignalOperation({
        spaceID: healthProSpace.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.RECIPES,
        lastActivityObjectID: recipesBoard.id,
      });
    }
    navigate(`/recipes/${firstRemainingRecipesBoard.id}`);
  };

  const onPublishRecipesBoardToSpace = async () => {
    const recipesBoard = ownedRecipesBoards.find(
      (b) => b.id === selectedRecipesBoardId,
    );
    return publishRecipesBoardToSpace(
      recipesBoard,
      healthProSpace,
      lazyGetFullMealsByParent,
      lazyGetDistilledSharedMealsByParent,
      createSharedBoard,
      createSharedMeal,
      deleteSharedMeal,
      updateSmorgBoard,
      updateSharedBoard,
      updateSpace,
      dispatch,
    );
  };

  const onUnpublishRecipesBoardFromSpace = async () => {
    await unpublishRecipesBoardFromSpace(
      selectedRecipesBoard,
      healthProSpace,
      updateSpace,
      dispatch,
    );
  };

  // const isSharedProgrammeBoard = [
  //   ContainerType.PROGRAMME,
  //   ContainerType.SHARED_PROGRAMME,
  // ].includes(selectedRecipesBoard?.embeddedInContainerType);

  const { username: myID } = useSelector(currentUserSelector);

  const otherActiveUser = useSelector((state) =>
    /* isSharedProgrammeBoard
      ? otherUserActiveOnResourceSelector(
          state,
          myID,
          ActivityObjectType.PROGRAMMES,
          selectedRecipesBoard.embeddedInContainerID,
        )
      : */ otherUserActiveOnResourceSelector(
      state,
      myID,
      ActivityObjectType.RECIPES,
      selectedRecipesBoardId,
    ),
  );

  const isPublishedToSpace = isBoardPublishedToSpace(
    selectedRecipesBoard,
    healthProSpace,
  );

  return (
    <Box
      display="flex"
      className="plannerNavigationBar"
      justifyContent="center"
      style={{ color: 'var(--app-primary-font-color)' }}>
      <div style={{ margin: 'auto 0', padding: '0 10px' }}>
        {selectedRecipesBoardTitle}
      </div>
      {otherActiveUser && (
        <div style={{ marginLeft: '20px', display: 'flex' }}>
          <Tooltip title="Modifying this board whilst another user is active may cause data loss">
            <WarningIcon
              style={{
                margin: 'auto 10px auto 0',
                color: 'orange',
              }}
            />
          </Tooltip>
          <div style={{ margin: 'auto 0', fontSize: '14px' }}>
            {otherActiveUser} is currently editing this board!
          </div>
        </div>
      )}
      <div style={{ flexGrow: 1, margin: 'auto 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            margin: 'auto 0',
          }}
        />
      </div>
      {isPublishedToSpace && (
        <div
          style={{
            margin: 'auto 6px auto 0',
            padding: '4px',
            fontSize: '14px',
            backgroundColor: 'white',
            color: '#508f55',
          }}>
          Published
        </div>
      )}
      <div style={{ padding: '0 10px' }}>
        <RecipeBoardActionsPopupMenu
          sharedProgrammesLinked={sharedProgrammesLinked}
          onEditRecipesBoard={onEditRecipesBoard}
          onDuplicateRecipesBoard={onDuplicateRecipesBoard}
          onShareRecipesBoard={onShareRecipesBoard}
          onDeleteRecipesBoard={onDeleteRecipesBoard}
          editBoardDisabled={
            isRecipesBoardEmbeddedInProgramme ||
            // isSharedProgrammeBoard ||
            isProductizedSharedBoard
          }
          duplicateBoardDisabled={
            isRecipesBoardEmbeddedInProgramme ||
            // isSharedProgrammeBoard ||
            isProductizedSharedBoard
          }
          shareBoardDisabled={
            isRecipesBoardEmbeddedInProgramme ||
            // isSharedProgrammeBoard ||
            isProductizedSharedBoard
          }
          deleteBoardDisabled={
            isRecipesBoardEmbeddedInProgramme ||
            // isSharedProgrammeBoard ||
            !ownedRecipesBoards ||
            ownedRecipesBoards.length <= 1 ||
            selectedRecipesBoard?.isMyFavouritesBoard
          }
          onPublishRecipesBoardToSpace={onPublishRecipesBoardToSpace}
          onUnpublishRecipesBoardFromSpace={onUnpublishRecipesBoardFromSpace}
        />
      </div>
      {recipesBoardEditMode && (
        <RecipesBoardEditModal
          initialBoard={selectedRecipesBoard}
          onSave={updateRecipesBoard}
          onDismiss={() => setRecipesBoardEditMode(false)}
        />
      )}
    </Box>
  );
};

RecipesNavigationBar.propTypes = {
  selectedRecipesBoardId: PropTypes.string.isRequired,
  openInRenameMode: PropTypes.bool,
};

RecipesNavigationBar.defaultProps = {
  openInRenameMode: false,
};

export default RecipesNavigationBar;
